import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import {
  MedicalOpinionCloseReason,
  MedicalOpinionCloseReasonOption,
  MedicalOpinionType,
  MedicalOpinionTypeOption,
} from "../types/api";

/**
 * The medical opinion (מכתב לגורם חיצוני) dropdown lists, served by the backend so the Hebrew labels
 * have a single source of truth.
 *
 * Both lists are static clinic vocabulary, so they are fetched once and never refetched -
 * five components need them (the create modal, the close modal, the approval modal, the
 * patient page list and the task screen) and React Query's cache is what stops that being
 * five requests.
 *
 * The token is read here rather than passed in, so the lists do not have to be drilled
 * through the approval modal into the close modal. Same pattern as CreateTaskModal and
 * UpdateInternalTaskModal.
 */

const STATIC_LIST_QUERY = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
  retry: 1,
};

// Stable identities, so a failed or in-flight fetch does not re-run the label memo on every
// render.
const NO_TYPES: MedicalOpinionTypeOption[] = [];
const NO_CLOSE_REASONS: MedicalOpinionCloseReasonOption[] = [];

const toLabelMap = <T extends string>(
  options: { value: T; label: string }[]
): Partial<Record<T, string>> =>
  options.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {} as Partial<Record<T, string>>);

export const useMedicalOpinionTypes = () => {
  const token = localStorage.getItem("token") || "";

  const query = useQuery({
    queryKey: ["medicalOpinionTypes"],
    queryFn: () => apiService.getMedicalOpinionTypes(token),
    enabled: !!token,
    ...STATIC_LIST_QUERY,
  });

  const options = query.data ?? NO_TYPES;
  // For rendering a stored opinion_type back as its label. Call sites fall back to the raw
  // value, so a failed fetch degrades to "sal_shikum" rather than to a blank cell.
  const labels = useMemo(
    () => toLabelMap<MedicalOpinionType>(options),
    [options]
  );

  return { options, labels, isLoading: query.isLoading, isError: query.isError };
};

export const useMedicalOpinionCloseReasons = () => {
  const token = localStorage.getItem("token") || "";

  const query = useQuery({
    queryKey: ["medicalOpinionCloseReasons"],
    queryFn: () => apiService.getMedicalOpinionCloseReasons(token),
    enabled: !!token,
    ...STATIC_LIST_QUERY,
  });

  const options = query.data ?? NO_CLOSE_REASONS;
  const labels = useMemo(
    () => toLabelMap<MedicalOpinionCloseReason>(options),
    [options]
  );

  return { options, labels, isLoading: query.isLoading, isError: query.isError };
};
